import { CENSUS_API, LS_JURISDICTION, US_STATES } from './constants.js'

// UX49: per-pubkey jurisdiction shadow. Lets a user sign out and back
// in (encrypted key, Google, whatever) and have their saved location
// restored even without a Drive round-trip. Keyed under one LS entry:
//   { '<hex pubkey>': jurisdiction, ... }
const LS_JURISDICTION_BY_PUBKEY = 'verdant_jurisdiction_by_pubkey'

export function loadJurisdiction() {
  try {
    const raw = localStorage.getItem(LS_JURISDICTION)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export function saveJurisdiction(j) {
  localStorage.setItem(LS_JURISDICTION, JSON.stringify(j))
}

export function clearJurisdiction() {
  localStorage.removeItem(LS_JURISDICTION)
}

function loadJurisdictionMap() {
  try {
    const raw = localStorage.getItem(LS_JURISDICTION_BY_PUBKEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

export function loadJurisdictionForPubkey(pubkey) {
  if (!pubkey) return null
  const map = loadJurisdictionMap()
  return map[pubkey] || null
}

export function saveJurisdictionForPubkey(pubkey, j) {
  if (!pubkey) return
  try {
    const map = loadJurisdictionMap()
    if (j) {
      map[pubkey] = j
    } else {
      delete map[pubkey]
    }
    localStorage.setItem(LS_JURISDICTION_BY_PUBKEY, JSON.stringify(map))
  } catch { /* noop */ }
}

export function stateName(code) {
  const found = US_STATES.find(([c]) => c === code)
  return found ? found[1] : code
}

// Build the jurisdiction stack array used by the header pill and panel.
// Returns labels like ["United States", "Washington", "Seattle", "WA-7th District"]
export function jurisdictionStack(j) {
  if (!j) return []
  const out = ['United States']
  if (j.state) out.push(stateName(j.state))
  if (j.city) out.push(j.city)
  if (j.district) out.push(`${j.state}-${j.district} District`)
  return out
}

// Compact label for the header pill, e.g. "Seattle, WA".
export function jurisdictionPillLabel(j) {
  if (!j) return ''
  if (j.city && j.state) return `${j.city}, ${j.state}`
  if (j.state) return stateName(j.state)
  return 'United States'
}

// Convert stored jurisdiction to a Nostr-tag string at the most specific
// level the user has set. Used when posting votes.
export function jurisdictionTagValue(j) {
  if (!j) return null
  if (j.city && j.state) return `US-${j.state}-${j.city}`
  if (j.state) return `US-${j.state}`
  return 'US'
}

// Public, no-API-key ZIP -> place lookup. Returns city, state, lat/lng.
const ZIPPO_API = 'https://api.zippopotam.us/us'

// Two-stage ZIP lookup:
//   1. Zippopotam.us:  ZIP -> { city, state, lat, lng }
//   2. Census Geocoder: lat/lng -> congressional district
// Either stage may fail independently; the function returns whatever
// it managed to resolve. The result object carries a `warn` field with
// a human-readable note the caller can surface so a partial result
// doesn't look like a silent failure.
const FETCH_TIMEOUT_MS = 8000

function withTimeout(signal) {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS)
  if (signal) {
    if (signal.aborted) ctrl.abort()
    else signal.addEventListener('abort', () => ctrl.abort(), { once: true })
  }
  return { signal: ctrl.signal, cancel: () => clearTimeout(timer) }
}

export async function lookupZip(zip, { signal } = {}) {
  if (!/^\d{5}$/.test(zip)) return null

  // --- stage 1: ZIP -> place ---
  let place
  const t1 = withTimeout(signal)
  try {
    const res = await fetch(`${ZIPPO_API}/${zip}`, { signal: t1.signal })
    t1.cancel()
    if (!res.ok) {
      console.warn('ZIP -> place lookup non-2xx', res.status)
      return { zip, warn: `ZIP service responded ${res.status}. Enter other fields manually.` }
    }
    const data = await res.json()
    place = data?.places?.[0]
  } catch (err) {
    t1.cancel()
    if (err?.name === 'AbortError' && signal?.aborted) throw err
    console.warn('ZIP -> place lookup failed', err)
    return { zip, warn: 'Could not reach ZIP service. Enter other fields manually.' }
  }
  if (!place) return { zip, warn: 'No match for that ZIP. Enter other fields manually.' }

  const stateCode = place['state abbreviation'] || null
  const city = place['place name'] || null
  const lat = parseFloat(place.latitude)
  const lng = parseFloat(place.longitude)

  const result = {
    zip,
    state: stateCode,
    city,
    district: null,
    matchedAddress: city && stateCode ? `${city}, ${stateCode} ${zip}` : null,
  }

  // --- stage 2: lat/lng -> congressional district ---
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    const t2 = withTimeout(signal)
    try {
      const params = new URLSearchParams({
        x: String(lng),
        y: String(lat),
        benchmark: 'Public_AR_Current',
        vintage: 'Current_Current',
        format: 'json',
        layers: 'Congressional Districts',
      })
      const res = await fetch(
        `${CENSUS_API}/geographies/coordinates?${params.toString()}`,
        { signal: t2.signal }
      )
      t2.cancel()
      if (res.ok) {
        const data = await res.json()
        const geographies = data?.result?.geographies || {}
        // Census prefixes the layer name with the current congress number
        // ("120th Congressional Districts") and bumps it every two years.
        // Find the key by pattern instead of hardcoding it.
        const cdKey = Object.keys(geographies).find((k) =>
          /Congressional Districts/i.test(k)
        )
        const d = cdKey ? geographies[cdKey]?.[0] : null
        if (d) {
          // BASENAME is the unpadded number ("7"); CD<NNN> is zero-padded
          // ("07"). Prefer BASENAME; fall back to any CD-prefixed field
          // regardless of Congress number so a session rollover doesn't
          // silently break this path.
          const cdFallback = Object.keys(d).find((k) => /^CD\d+/.test(k))
          const raw = d.BASENAME || (cdFallback && d[cdFallback]) || null
          if (raw) {
            result.district = String(raw).replace(/^0+(?=\d)/, '')
          }
        }
        if (!result.district) {
          result.warn = `Found ${result.city}, ${result.state}. Congressional district not resolved.`
        }
      } else {
        console.warn('Census responded non-2xx', res.status)
        result.warn = `Found ${result.city}, ${result.state}. District service responded ${res.status}.`
      }
    } catch (err) {
      t2.cancel()
      if (err?.name === 'AbortError' && signal?.aborted) throw err
      console.warn('Census district lookup failed', err)
      result.warn = `Found ${result.city}, ${result.state}. Could not reach district service.`
    }
  }

  return result
}
